import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  UserCog,
  Plus,
  MoreHorizontal,
  Edit2,
  Trash2,
  Loader2,
  Shield,
  ShieldCheck,
  ShieldAlert,
  ShieldOff
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { getErrorMessage } from "@/lib/utils";

export default function Users() {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetMFADialogOpen, setResetMFADialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "staff",
    location_ids: []
  });

  const fetchData = async () => {
    try {
      const [usersRes, locationsRes] = await Promise.all([
        axios.get(`${API}/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/locations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setUsers(usersRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        email: user.email,
        password: "",
        name: user.name,
        role: user.role,
        location_ids: user.location_ids || []
      });
    } else {
      setSelectedUser(null);
      // Set default role based on current user's permissions
      let defaultRole = "staff";
      if (currentUser?.role === "super_admin") {
        defaultRole = "staff"; // Super admin can choose, default to staff
      } else if (currentUser?.role === "main_office") {
        defaultRole = "staff"; // Main office defaults to staff
      } else if (currentUser?.role === "location_admin") {
        defaultRole = "staff"; // Location admin can only create staff
      }
      setFormData({
        email: "",
        password: "",
        name: "",
        role: defaultRole,
        location_ids: []
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.email || !formData.name) {
      toast.error("Email and name are required");
      return;
    }
    if (!selectedUser && !formData.password) {
      toast.error("Password is required for new users");
      return;
    }

    setSaving(true);
    try {
      if (selectedUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await axios.put(`${API}/users/${selectedUser.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("User updated successfully");
      } else {
        await axios.post(`${API}/users`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("User created successfully");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error saving user:", error);
      toast.error(getErrorMessage(error, "Failed to save user"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    setSaving(true);
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User deleted successfully");
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Failed to delete user");
    } finally {
      setSaving(false);
    }
  };

  const handleResetMFA = async () => {
    if (!selectedUser) return;

    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/auth/mfa/reset/${selectedUser.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setResetMFADialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error resetting MFA:", error);
      toast.error(getErrorMessage(error, "Failed to reset MFA"));
    } finally {
      setSaving(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "super_admin":
        return <ShieldAlert className="h-4 w-4 text-blue-600" />;
      case "main_office":
        return <ShieldCheck className="h-4 w-4 text-indigo-600" />;
      case "location_admin":
        return <ShieldCheck className="h-4 w-4 text-purple-600" />;
      default:
        return <Shield className="h-4 w-4 text-gray-400" />;
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: "bg-blue-100 text-blue-800",
      main_office: "bg-indigo-100 text-indigo-800",
      location_admin: "bg-purple-100 text-purple-800",
      staff: "bg-gray-100 text-gray-800"
    };
    const labels = {
      super_admin: "Super Admin",
      main_office: "Main Office",
      location_admin: "Location Admin",
      staff: "Staff"
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badges[role] || badges.staff}`}>
        {getRoleIcon(role)}
        {labels[role] || "Staff"}
      </span>
    );
  };

  // Check if current user can edit target user based on role hierarchy
  const canEditUser = (targetUser) => {
    // Users can always edit themselves
    if (targetUser.id === currentUser?.id) return true;
    
    const roleHierarchy = ["super_admin", "main_office", "location_admin", "staff"];
    const currentUserPriority = roleHierarchy.indexOf(currentUser?.role);
    const targetUserPriority = roleHierarchy.indexOf(targetUser.role);
    
    // Can only edit users with lower priority (higher index)
    return targetUserPriority > currentUserPriority;
  };

  // Check if current user can delete target user
  const canDeleteUser = (targetUser) => {
    // Cannot delete yourself
    if (targetUser.id === currentUser?.id) return false;
    
    // Only Super Admin and Main Office can delete
    if (currentUser?.role !== "super_admin" && currentUser?.role !== "main_office") return false;
    
    // Apply same hierarchy rules
    return canEditUser(targetUser);
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location?.name || "Unknown";
  };

  // Filter users based on search, role, and location
  const filteredUsers = users.filter((user) => {
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      if (!user.name.toLowerCase().includes(searchLower) && 
          !user.email.toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    
    // Role filter
    if (roleFilter !== "all" && user.role !== roleFilter) {
      return false;
    }
    
    // Location filter
    if (locationFilter !== "all") {
      // Super admin and main office have access to all locations
      if (user.role === "super_admin" || user.role === "main_office") {
        return true;
      }
      if (!user.location_ids?.includes(locationFilter)) {
        return false;
      }
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-500 mt-1">
            Manage system users and permissions
          </p>
        </div>
        {currentUser?.role !== "staff" && (
          <Button onClick={() => handleOpenDialog()} className="btn-press" data-testid="add-user-btn">
            <Plus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
                data-testid="search-input"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="role-filter">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="main_office">Main Office</SelectItem>
                <SelectItem value="location_admin">Location Admin</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
              </SelectContent>
            </Select>
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="location-filter">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Locations</TableHead>
                <TableHead>MFA</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <TableRow key={user.id} className="table-row-hover" data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell>
                      {user.location_ids?.length > 0
                        ? user.location_ids.map(id => getLocationName(id)).join(", ")
                        : (user.role === "super_admin" || user.role === "main_office") ? "All" : "-"}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        user.mfa_enabled
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}>
                        {user.mfa_enabled ? "Enabled" : "Disabled"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        user.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}>
                        {user.is_active ? "Active" : "Inactive"}
                      </span>
                    </TableCell>
                    <TableCell>
                      {format(parseISO(user.created_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell className="text-right">
                      {(canEditUser(user) || canDeleteUser(user) || (currentUser?.role === "super_admin" && user.mfa_enabled && user.id !== currentUser.id)) ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`user-actions-${user.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canEditUser(user) && (
                              <DropdownMenuItem onClick={() => handleOpenDialog(user)}>
                                <Edit2 className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                            )}
                            {/* Super Admin can reset MFA for other users */}
                            {currentUser?.role === "super_admin" && user.mfa_enabled && user.id !== currentUser.id && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-amber-600"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setResetMFADialogOpen(true);
                                  }}
                                  data-testid={`reset-mfa-${user.id}`}
                                >
                                  <ShieldOff className="mr-2 h-4 w-4" />
                                  Reset MFA
                                </DropdownMenuItem>
                              </>
                            )}
                            {canDeleteUser(user) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <UserCog className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500">No users found</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reset MFA Confirmation Dialog */}
      <Dialog open={resetMFADialogOpen} onOpenChange={setResetMFADialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset MFA for {selectedUser?.name}</DialogTitle>
            <DialogDescription>
              This will disable two-factor authentication for this user. They will be required to set up MFA again on their next sign in.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Warning:</strong> The user will not be able to access the application until they complete MFA setup on their next login.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetMFADialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="default"
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleResetMFA} 
              disabled={saving} 
              data-testid="confirm-reset-mfa-btn"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldOff className="mr-2 h-4 w-4" />}
              Reset MFA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{selectedUser ? "Edit User" : "Add User"}</DialogTitle>
            <DialogDescription>
              {selectedUser ? "Update user information and permissions" : "Add a new user to the system"}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">
                Password {selectedUser ? "(leave blank to keep current)" : "*"}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                data-testid="user-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Super Admin can create all roles */}
                  {currentUser?.role === "super_admin" && (
                    <>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                      <SelectItem value="main_office">Main Office</SelectItem>
                      <SelectItem value="location_admin">Location Admin</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                    </>
                  )}
                  {/* Main Office can create Location Admin or Staff */}
                  {currentUser?.role === "main_office" && (
                    <>
                      <SelectItem value="location_admin">Location Admin</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                    </>
                  )}
                  {/* Location Admin can only create Staff */}
                  {currentUser?.role === "location_admin" && (
                    <SelectItem value="staff">Staff</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            {formData.role !== "super_admin" && formData.role !== "main_office" && (
              <div className="space-y-2">
                <Label htmlFor="location">Assigned Location</Label>
                <Select
                  value={formData.location_ids[0] || ""}
                  onValueChange={(value) => setFormData({ ...formData, location_ids: value ? [value] : [] })}
                >
                  <SelectTrigger data-testid="user-location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-user-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {selectedUser ? "Save Changes" : "Add User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedUser?.name}"? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving} data-testid="confirm-delete-user-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
